import { DomSanitizer } from '@angular/platform-browser';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    Subject,
    Observable,
    firstValueFrom,
    concat,
    ObservableInput,
    EMPTY,
    BehaviorSubject,
    NEVER,
    OperatorFunction,
    connectable,
    ReplaySubject
} from 'rxjs';
import { normalClosureMessage } from 'rxjs-websockets';
import { QueueingSubject } from 'queueing-subject';
import {
    map,
    shareReplay,
    filter,
    takeUntil,
    distinctUntilChanged,
    scan,
    share,
    switchMap,
    catchError,
    take,
    skip,
    concatAll
} from 'rxjs/operators';
import logger from 'loglevel';
import { ReadonlyDeep } from 'type-fest';

import { websocketConnect } from '@flb/common/websocket-connect-wrapper';
import { WsStatus, ConnectionViolation } from '@flb/common/ws-status';
import { ProcessMetrics } from '@flb/common/process-metrics';
import { CensoredTeamState, PrunedTeamState } from '@flb/common/team';
import {
    TeamAndProgress,
    teamAndProgressRankSort,
    updateTeamAndProgressMap
} from '@flb/common/team-progress';
import {
    ConfigurationState,
    EscapeRoomTargetDescription,
    LeaderboardWorkshopInstance
} from '@flb/common/configuration';
import { isDeleted, MaybeDeleted } from '@flb/common/deleted';
import { ClientId, ClientState } from '@flb/common/client';
import { TrainingSessionTransportState } from '@flb/common/training-session-transport';
import { FndnUserInfoMap } from '@flb/common/fndn-user-info';
import { Counter, CounterAccumulator } from '@flb/common/counter-stats/counter';
import { shareConfigPublishReplay } from '@flb/common/share-config-publish-replay';
import {
    assertIsError,
    assertNotNullish,
    deepApply,
    errorIfNullish,
    hasKey,
    notNullish,
    objectCompare
} from '@flb/common/util';
import { AccessLevel } from '@flb/common/access-level';
import { TeamId } from '@flb/common/team-id';
import { ObjectivePackageInfo } from '@flb/common/objective-package';
import { EventMode } from '@flb/common/event-mode';
import { webSocketStatusString } from '@flb/common/websocket-status';
import { ClientTrackingState, RoomLocationInfo } from '@flb/common/client-tracking-state';
import { PlayerAssetsMap } from '@flb/common/player-assets';
import { LoadTestStatus } from '@flb/common/load-test-status';
import { AnySupportRequestUpdate } from '@flb/common/support-request';
import { AuthToken } from '@flb/common/auth-token';

import { ClientStateService } from './client-state.service.js';
import { SafeAnchor } from './safe-anchor.js';

export interface StreamData {
    data: Observable<WsStatus>;
    mergedData: Observable<WsStatus>;
    config: Observable<ConfigurationState>;
    highScore: Observable<number>; // Season high score
    leaderScore: Observable<number>; // Leaderboard high score
    clues: Observable<EscapeRoomTargetDescription[]>;
    training: Observable<TrainingSessionTransportState>;
    clients: Observable<ClientState[]>;
    userInfo: Observable<FndnUserInfoMap>;
    playerAssets: Observable<PlayerAssetsMap>;
    objectiveLinks: Observable<ObjectivePackageInfo<SafeAnchor>>;
    eventMode: Observable<EventMode>;
    memory: Observable<ProcessMetrics[]>;
    load: Observable<ProcessMetrics[]>;
    cpu: Observable<ProcessMetrics[]>;
    socket: Observable<ProcessMetrics[]>;
    metrics: Observable<ProcessMetrics[]>;
    counters: Observable<CounterAccumulator>;
    workshopInstances: Observable<LeaderboardWorkshopInstance[]>;
    uploadedToFNDN: Observable<number>; // Time scores were uploaded to FNDN
    loadTest: Observable<LoadTestStatus>; // Whether a load test is currently running
    supportRequests: Observable<AnySupportRequestUpdate>;
    supportRequestsWaiting: Observable<AnySupportRequestUpdate[]>;
    roomLocations: Observable<RoomLocationInfo[]>;
    connectionViolations: Observable<ConnectionViolation[]>;
}

export interface TeamProgressUpdates {
    id: number;
    tpUpdates: Observable<TeamAndProgress<CensoredTeamState>>;
}
export interface StreamDataWithProgress extends StreamData {
    teamsProgress: Observable<Array<TeamAndProgress<CensoredTeamState>>>;
    observableTeamsProgress: Observable<Array<TeamProgressUpdates>>;
    // This version of teamProgress only emits uncensored data.
    uncensoredTeamsProgress: Observable<Array<TeamAndProgress<PrunedTeamState>>>;
}

@Injectable({
    providedIn: 'root'
})
export class StateStreamService implements OnDestroy {
    // these streams will end when the current conectUntil's destroy is updated.
    // they will be replaced when connectUntil() is called again.
    private _streams = new BehaviorSubject<StreamData>({
        data: NEVER,
        mergedData: NEVER,
        config: NEVER,
        highScore: NEVER,
        leaderScore: NEVER,
        clues: NEVER,
        training: NEVER,
        clients: NEVER,
        userInfo: NEVER,
        playerAssets: NEVER,
        objectiveLinks: NEVER,
        eventMode: NEVER,
        memory: NEVER,
        cpu: NEVER,
        load: NEVER,
        socket: NEVER,
        metrics: NEVER,
        counters: NEVER,
        workshopInstances: NEVER,
        uploadedToFNDN: NEVER,
        loadTest: NEVER,
        supportRequests: NEVER,
        supportRequestsWaiting: NEVER,
        roomLocations: NEVER,
        connectionViolations: NEVER
    });

    // TODO: remove all these properties, should use connectUntil().xyz instead!
    config = this._streams.pipe(switchMap(d => d.config));
    userInfo = this._streams.pipe(switchMap(d => d.userInfo));
    highScore = this._streams.pipe(switchMap(d => d.highScore));
    leaderScore = this._streams.pipe(switchMap(d => d.leaderScore));
    training = this._streams.pipe(switchMap(d => d.training));
    counters = this._streams.pipe(switchMap(d => d.counters));
    clients = this._streams.pipe(switchMap(d => d.clients));
    eventMode = this._streams.pipe(switchMap(d => d.eventMode));
    loadTest = this._streams.pipe(switchMap(d => d.loadTest));
    supportRequestsWaiting = this._streams.pipe(switchMap(d => d.supportRequestsWaiting));
    supportRequests = this._streams.pipe(switchMap(d => d.supportRequests));
    connectionViolations = this._streams.pipe(switchMap(d => d.connectionViolations));

    output: QueueingSubject<object>;
    connectionStatus!: Observable<string>;
    private destroyed = new Subject<void>();
    private window: Window;
    // only for testing
    websocketFactory?: Parameters<typeof websocketConnect>[3];
    constructor(
        private http: HttpClient,
        private clientState: ClientStateService,
        protected sanitizer: DomSanitizer,
        @Inject(DOCUMENT) document: Document
    ) {
        this.output = new QueueingSubject<object>();
        assertNotNullish(document.defaultView);
        this.window = document.defaultView;
    }

    ngOnDestroy(): void {
        this.destroyed.next();
    }

    getWsOrigin() {
        // https:// => wss://, http:// => ws://
        return `ws${this.window.location.origin.replace(/^http/, '')}`;
    }

    erConnectUntil<T>(
        clientSecret: string,
        destroy: Subject<void>,
        output?: Observable<object> | null,
        path = 'ws'
    ): Observable<T> {
        if (!output) {
            output = new QueueingSubject<object>();
        }
        const { messages, connectionStatus } = websocketConnect<string>(
            `${this.getWsOrigin()}/api/escaperoom/${path}`,
            output.pipe(map(v => JSON.stringify(v))),
            clientSecret,
            this.websocketFactory
        );
        const latestData = messages.pipe(
            map(m => {
                // keep this in a block so it can be debugged
                return <T>JSON.parse(m);
            }),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const mergedData = latestData.pipe(scan((m, n) => ({ ...m, ...n }), {} as T));
        const data = concat(mergedData.pipe(take(1)), latestData.pipe(skip(1)));
        firstValueFrom(
            connectionStatus.pipe(
                filter(status => status === WebSocket.OPEN),
                takeUntil(destroy)
            )
        ).then(logger.log, logger.error);
        return data;
    }

    // player only stream for support request updates.
    supportRequestConnectUntil(
        auth: Observable<AuthToken | null>,
        destroy: Observable<void>
    ): Observable<AnySupportRequestUpdate> {
        const output = auth.pipe(
            filter(notNullish),
            filter(t => t.accessLevel === AccessLevel.Player),
            map(({ id }) => ({ authorization: id }))
        );
        const { messages, connectionStatus } = websocketConnect<string>(
            `${this.getWsOrigin()}/api/support-requests/ws`,
            output.pipe(map(v => JSON.stringify(v))),
            undefined,
            this.websocketFactory
        );
        firstValueFrom(
            connectionStatus.pipe(
                filter(status => status === WebSocket.OPEN),
                takeUntil(destroy)
            )
        ).catch(ex => logger.error('supportRequestConnectUntil', ex));
        return messages.pipe(
            map(s => JSON.parse(s) as AnySupportRequestUpdate),
            takeUntil(destroy),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    connectUntil(
        destroy: Observable<void>,
        trackProgress?: false | undefined,
        reloadOnError?: boolean,
        accessLevel?: AccessLevel
    ): StreamData;
    connectUntil(
        destroy: Observable<void>,
        trackProgress: true,
        reloadOnError?: boolean,
        accessLevel?: AccessLevel
    ): StreamDataWithProgress;
    connectUntil(
        destroy: Observable<void>,
        trackProgress = false,
        reloadOnError = true,
        accessLevel = AccessLevel.Admin
    ): StreamData | StreamDataWithProgress {
        this.output = new QueueingSubject<object>();
        const RELOAD_DELAY = 2500;
        const MAX_RETRIES = 50;
        const { messages, connectionStatus } = websocketConnect<string>(
            `${this.getWsOrigin()}/api/state/ws`,
            this.output.asObservable().pipe(
                map(v => JSON.stringify(v)),
                share(shareConfigPublishReplay())
            ),
            undefined,
            this.websocketFactory
        );
        const handleError = (err: unknown): ObservableInput<WsStatus> => {
            assertIsError(err);
            if (err.message !== normalClosureMessage) {
                // Empty message means a normal close
                try {
                    throw new Error(err.message);
                } catch (err2) {
                    assertIsError(err2);
                    logger.error(
                        `Websocket Error: ${err.message}\n${String(err2.stack)
                            .split('\n')
                            .slice(0, 3)
                            .join('\n')}\n${String(err.stack).split('\n').slice(0, 3).join('\n')}`
                    );
                }
            } else {
                logger.log('Websocket closed normally: ', err.message);
            }
            // an error in the websocket usually means we need to reload the whole page because
            // the server restarted
            if (reloadOnError) {
                (async () => {
                    let retryCount = 0;
                    await new Promise(resolve => setTimeout(resolve, RELOAD_DELAY));
                    do {
                        try {
                            logger.log(
                                'Ready to reload: ',
                                await firstValueFrom(
                                    this.http.get(this.window.location.href, {
                                        headers: {
                                            Accept: 'text/html'
                                        },
                                        responseType: 'text'
                                    })
                                )
                            );
                            debugger;
                            this.window.location.reload();
                            break;
                        } catch (ex) {
                            await new Promise(resolve => setTimeout(resolve, RELOAD_DELAY));
                            logger.warn(
                                `Can't reload yet: ${
                                    (ex as Error)?.message || (ex as Error)?.stack || ex
                                }`
                            );
                        }
                    } while (retryCount++ < MAX_RETRIES);
                    if (retryCount > MAX_RETRIES) {
                        logger.error(
                            `Tried to reload too many times, giving up after ${retryCount}`
                        );
                    }
                })().catch(logger.error);
            }
            return EMPTY;
        };
        const latestData = messages.pipe(
            map(m => <WsStatus>JSON.parse(m)),
            filter(m => m.access === accessLevel),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const mergedData = latestData.pipe(
            scan((m, n) => ({ ...m, ...n }), {} as WsStatus),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const data = concat(mergedData.pipe(take(1)), latestData.pipe(skip(1))).pipe(
            catchError(handleError)
            // don't share this because every subscription needs to take(1)
            // from mergedData before it reads the rest from latestData..
        );
        firstValueFrom(
            connectionStatus.pipe(
                filter(status => status === WebSocket.OPEN),
                takeUntil(destroy)
            )
        )
            .then(() => {
                if (
                    this.clientState.authToken.value &&
                    [AccessLevel.Admin, AccessLevel.Readonly, AccessLevel.Viewer].includes(
                        accessLevel
                    )
                ) {
                    this.output.next({
                        authorization: `Bearer ${this.clientState.authToken.value?.id}`,
                        accessLevel
                    });
                }
            })
            .catch(logger.error);
        const config = data.pipe(
            filter(hasKey('config')),
            map(m => m.config),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        type TP = TeamAndProgress<PrunedTeamState | CensoredTeamState>;
        interface TPMaps {
            tp: Map<TeamId, TP>;
            tpbs: Map<TeamId, BehaviorSubject<TP>>;
            sorted: TP[];
        }
        const tpsScanInit: TPMaps = {
            tp: new Map<TeamId, TP>(),
            tpbs: new Map<TeamId, BehaviorSubject<TP>>(),
            sorted: []
        };
        const teamsProgressMaps = data.pipe(
            filter(hasKey('progress')),
            scan((acc, m) => {
                const updated = m.progress
                    .filter(tp => !('deleted' in tp))
                    .map(tp => ('id' in tp ? tp.id : tp.team.id));
                const deleted = m.progress.filter(isDeleted).map(tp => tp.id);
                updateTeamAndProgressMap(acc.tp, m.progress);
                for (const id of updated) {
                    const bs = acc.tpbs.get(id);
                    const tp = acc.tp.get(id)!;
                    if (bs) {
                        bs.next(tp);
                    } else {
                        acc.tpbs.set(id, new BehaviorSubject<TP>(tp));
                    }
                }
                for (const id of deleted) {
                    // Complete the BehaviorSubject before removing so subscribers unsubscribe
                    // and the subject can be garbage collected (prevents memory leak)
                    acc.tpbs.get(id)?.complete();
                    acc.tpbs.delete(id);
                }
                return {
                    ...acc,
                    sorted: Array.from(acc.tp.values()).sort(teamAndProgressRankSort)
                };
            }, tpsScanInit),
            catchError((ex: unknown, caught) => {
                assertIsError(ex);
                logger.error(`Error processing teamAndProgress:\n${ex.message}\n${ex.stack}`);
                return caught;
            }),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const teamsProgressMapsConnectable = trackProgress
            ? connectable(teamsProgressMaps, {
                  connector() {
                      return new ReplaySubject<TPMaps>(1);
                  }
              })
            : null;
        // we have to connect immediately so we don't miss any messages
        teamsProgressMapsConnectable?.connect();
        const teamsProgress = teamsProgressMapsConnectable?.pipe(map(({ sorted }) => sorted));
        const orderString = (sorted: TP[]) =>
            sorted.map(tp => `${tp.team.id}:${tp.progress.rank}`).join(',');
        const observableTeamsProgress = teamsProgressMapsConnectable?.pipe(
            distinctUntilChanged((a, b) => orderString(a.sorted) === orderString(b.sorted)),
            map(({ tpbs, sorted }): TeamProgressUpdates[] =>
                sorted
                    .filter(s => s.team.name)
                    .map(tp => ({
                        id: tp.team.id,
                        tpUpdates: errorIfNullish(tpbs.get(tp.team.id))
                    }))
            ),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const uncensoredTeamsProgress = teamsProgress?.pipe(
            map(tps =>
                tps.filter((tp): tp is TeamAndProgress<PrunedTeamState> => 'fortigateSn' in tp.team)
            )
        );
        const highScore = data.pipe(
            filter(hasKey('highScore')),
            map(m => m.highScore),
            distinctUntilChanged(),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const leaderScore = data.pipe(
            filter(hasKey('leaderScore')),
            map(m => m.leaderScore),
            distinctUntilChanged(),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const clues = data.pipe(
            filter(hasKey('clues')),
            map(m => m.clues),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const userInfo = data.pipe(
            filter(hasKey('userInfo')),
            map(m => m.userInfo),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const playerAssets = data.pipe(
            filter(hasKey('playerAssets')),
            map(m => m.playerAssets),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const memory = data.pipe(
            filter(hasKey('memory')),
            map(m => m.memory),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const cpu = data.pipe(
            filter(hasKey('cpu')),
            map(m => m.cpu),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const load = data.pipe(
            filter(hasKey('load')),
            map(m => m.load),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const socket = data.pipe(
            filter(hasKey('socket')),
            map(m => m.socket),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const metrics = data.pipe(
            filter(hasKey('metrics')),
            map(m => m.metrics),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const counters = data.pipe(
            filter(hasKey('counters')),
            map(m => Counter.accFromState(m.counters)),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const objectiveLinks = data.pipe(
            filter(hasKey('objectiveLinks')),
            map(m => m.objectiveLinks),
            map(ol => {
                return {
                    ...ol,
                    objectives: Object.entries(ol.objectives)
                        .map(
                            ([oid, anchors]) =>
                                [
                                    oid,
                                    anchors.map(({ link, text }) => ({
                                        link: this.sanitizer.bypassSecurityTrustUrl(link),
                                        text
                                    }))
                                ] as const
                        )
                        .reduce(
                            (r, [k, v]) => {
                                r[k] = v;
                                return r;
                            },
                            {} as Record<string, SafeAnchor[]>
                        )
                };
            }),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const clients = connectable(
            data.pipe(
                filter(hasKey('clients')),
                map(m => m.clients),
                scan((clientMap, clients) => {
                    for (const client of clients) {
                        if (isDeleted(client)) {
                            clientMap.delete(client.id);
                        } else {
                            clientMap.set(client.id, client);
                        }
                    }
                    return clientMap;
                }, new Map<ClientId, ClientState>()),
                map(clients => Array.from(clients.values())),
                takeUntil(destroy)
            ),
            {
                connector() {
                    return new BehaviorSubject<ClientState[]>([]);
                }
            }
        );
        // we have to connect immediately so we don't miss any messages
        clients.connect();
        const training = this.messageStream<TrainingSessionTransportState>(
            data,
            'training',
            destroy
        );
        const eventMode = this.messageStream<EventMode>(data, 'eventMode', destroy);

        const workshopInstances = this.messageStream<LeaderboardWorkshopInstance[]>(
            data,
            'workshopInstances',
            destroy
        );

        const uploadedToFNDN = data.pipe(
            filter(hasKey('uploadedToFNDN')),
            map(m => m.uploadedToFNDN),
            distinctUntilChanged(),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const loadTest = data.pipe(
            filter(hasKey('loadTest')),
            map(m => m.loadTest),
            distinctUntilChanged(objectCompare),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const supportRequests = data.pipe(
            filter(hasKey('supportRequests')),
            map(m => m.supportRequests),
            concatAll(),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const supportRequestsWaiting = data.pipe(
            filter(hasKey('supportRequestsWaiting')),
            map(m => m.supportRequestsWaiting),
            scan((map, srus) => {
                for (const sru of srus) {
                    if ('deleted' in sru) {
                        map.delete(sru.id);
                    } else {
                        map.set(sru.id, sru);
                    }
                }
                return map;
            }, new Map<string, AnySupportRequestUpdate>()),
            map(m => [...m.values()]),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const connectionViolations = data.pipe(
            filter(hasKey('connectionViolations')),
            map(m => m.connectionViolations || []),
            scan((acc, violations) => {
                for (const v of violations) {
                    const idx = acc.findIndex(a => a.teamId === v.teamId);
                    if (idx !== -1) {
                        acc[idx] = v;
                    } else {
                        acc.push(v);
                    }
                }
                return acc;
            }, [] as ConnectionViolation[]),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        const roomLocations = data.pipe(
            filter(hasKey('roomLocations')),
            map(m => m.roomLocations),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        const result = {
            data,
            mergedData,
            config,
            highScore,
            leaderScore,
            clues,
            training,
            userInfo,
            playerAssets,
            objectiveLinks,
            eventMode,
            counters,
            metrics,
            memory,
            cpu,
            load,
            socket,
            workshopInstances,
            uploadedToFNDN,
            clients,
            loadTest,
            supportRequests,
            supportRequestsWaiting,
            roomLocations,
            connectionViolations
        } as StreamData | StreamDataWithProgress;
        // streams that are reconstructed must use connectable.connect()
        // and shareReplay must be after that
        if (teamsProgress && uncensoredTeamsProgress && observableTeamsProgress) {
            const tpResult = result as StreamDataWithProgress;
            tpResult.teamsProgress = teamsProgress;
            tpResult.uncensoredTeamsProgress = uncensoredTeamsProgress;
            tpResult.observableTeamsProgress = observableTeamsProgress;
        }
        this._streams.next(result);

        this.connectionStatus = connectionStatus.pipe(
            map(webSocketStatusString) as OperatorFunction<number, string>
        );
        return result;
    }
    trackingConnectUntil(
        destroy: Subject<void>,
        accessLevel = AccessLevel.Admin
    ): Observable<ReadonlyMap<ClientId, ReadonlyDeep<ClientTrackingState>>> {
        const output = new QueueingSubject<string>();
        const { messages, connectionStatus } = websocketConnect<string>(
            `ws${this.window.location.origin.replace(/^http/, '')}/api/state/client-tracking`,
            output,
            undefined,
            this.websocketFactory
        );
        firstValueFrom(
            connectionStatus.pipe(
                filter(status => status === WebSocket.OPEN),
                takeUntil(destroy)
            )
        )
            .then(() => {
                if (
                    this.clientState.authToken.value &&
                    [AccessLevel.Admin, AccessLevel.Readonly].includes(accessLevel)
                ) {
                    output.next(
                        JSON.stringify({
                            authorization: `Bearer ${this.clientState.authToken.value?.id}`,
                            accessLevel
                        })
                    );
                }
            })
            .catch(logger.error);
        return messages.pipe(
            map(m => JSON.parse(m) as MaybeDeleted<ClientTrackingState>[]),
            scan((acc, items) => {
                for (const next of items) {
                    const ct = acc.get(next.id);
                    if ('deleted' in next) {
                        acc.delete(next.id);
                    } else if (ct) {
                        acc.set(next.id, deepApply(ct, next));
                    } else {
                        acc.set(next.id, next);
                    }
                }
                return acc;
            }, new Map<ClientId, ClientTrackingState>())
        );
    }

    private messageStream<T>(
        source: Observable<WsStatus>,
        messageKey: keyof WsStatus,
        destroy: Observable<void>
    ) {
        return source.pipe(
            filter(hasKey(messageKey)),
            map(m => m[messageKey] as unknown as T),
            distinctUntilChanged(),
            takeUntil(destroy),
            shareReplay({ bufferSize: 1, refCount: false })
        );
    }
}
